import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { AuthContext } from "./AuthContext";

export const SocketContext = createContext(null);

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "";

export default function SocketProvider({ children }) {
  const { player, isAuthenticated } = useContext(AuthContext);
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connectedCount, setConnectedCount] = useState(0);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
        setConnectedCount(0);
      }
      return;
    }

    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return;

    let cancelled = false;

    (async () => {
      const { io } = await import("socket.io-client");
      if (cancelled) return;

      const newSocket = io(SOCKET_URL, {
        auth: { token: accessToken },
        transports: ["websocket", "polling"],
      });

      if (cancelled) {
        newSocket.disconnect();
        return;
      }

      newSocket.on("connect", () => setConnected(true));
      newSocket.on("disconnect", () => {
        setConnected(false);
        setConnectedCount(0);
      });
      newSocket.on("shoutbox:connected-count", ({ count }) => {
        setConnectedCount(Number.isFinite(count) ? count : 0);
      });
      newSocket.on("connect_error", (err) => {
        console.error("Socket connection error:", err.message);
        setConnected(false);
      });

      socketRef.current = newSocket;
      setSocket(newSocket);
    })();

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocket(null);
      setConnected(false);
      setConnectedCount(0);
    };
  }, [isAuthenticated]);

  const sendMessage = useCallback((content, { replyToMessageId } = {}) => {
    if (socketRef.current?.connected) {
      const payload = { content };
      if (replyToMessageId != null) {
        payload.replyToMessageId = replyToMessageId;
      }
      socketRef.current.emit("message:send", payload);
    }
  }, []);

  const deleteMessage = useCallback(
    (id) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit("message:delete", { id });
      }
    },
    []
  );

  return (
    <SocketContext.Provider value={{ socket, connected, connectedCount, sendMessage, deleteMessage }}>
      {children}
    </SocketContext.Provider>
  );
}
